import { useMemo } from 'react'
import gojuonRaw from '../data/gojuon.json'
import { playAudio } from '../utils/audio'

interface RawEntry {
  id: string
  front: string
  back: string
  type: 'hiragana' | 'katakana'
}

interface GojuonCell {
  kana: string
  romaji: string
}

interface Props {
  onClose: () => void
}

/**
 * 从五十音数据构建 5 列（a/i/u/e/o）× 行 的标准五十音网格。
 * 空格子填充 null。
 */
function buildGrid(): (GojuonCell | null)[][] {
  const hiragana = (gojuonRaw as RawEntry[]).filter((e) => e.type === 'hiragana')

  // 按行首辅音分组，建立 romaji → kana 的映射
  const byRomaji = new Map<string, string>()
  for (const e of hiragana) {
    byRomaji.set(e.back, e.front)
  }

  // 经典五十音图：每行一个辅音组，每列一个元音段
  const rows: { label: string; cells: (string | null)[] }[] = [
    { label: 'あ', cells: ['a', 'i', 'u', 'e', 'o'] },
    { label: 'か', cells: ['ka', 'ki', 'ku', 'ke', 'ko'] },
    { label: 'さ', cells: ['sa', 'shi', 'su', 'se', 'so'] },
    { label: 'た', cells: ['ta', 'chi', 'tsu', 'te', 'to'] },
    { label: 'な', cells: ['na', 'ni', 'nu', 'ne', 'no'] },
    { label: 'は', cells: ['ha', 'hi', 'fu', 'he', 'ho'] },
    { label: 'ま', cells: ['ma', 'mi', 'mu', 'me', 'mo'] },
    { label: 'や', cells: ['ya', null, 'yu', null, 'yo'] },
    { label: 'ら', cells: ['ra', 'ri', 'ru', 're', 'ro'] },
    { label: 'わ', cells: ['wa', null, null, null, 'wo'] },
  ]

  const grid: (GojuonCell | null)[][] = []

  for (const row of rows) {
    const gridRow: (GojuonCell | null)[] = []
    for (const key of row.cells) {
      if (key === null) {
        gridRow.push(null)
      } else {
        const kana = byRomaji.get(key) ?? ''
        gridRow.push({ kana, romaji: key })
      }
    }
    grid.push(gridRow)
  }

  // 最后一行：ん
  grid.push([
    { kana: byRomaji.get('n') ?? 'ん', romaji: 'n' },
    null, null, null, null,
  ])

  return grid
}

export default function GojuonModal({ onClose }: Props) {
  const grid = useMemo(() => buildGrid(), [])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-zinc-950/95 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg mx-4 my-4 p-5 rounded-2xl bg-zinc-900 border border-white/5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white text-lg transition-all duration-150"
          title="关闭"
        >
          ✕
        </button>

        <h2 className="text-base font-bold text-white tracking-wide mb-5">
          🗺️ 五十音总览
        </h2>

        {/* 网格 */}
        <div className="flex flex-col gap-1">
          {grid.map((row, ri) => (
            <div key={ri} className="flex gap-1">
              {row.map((cell, ci) =>
                cell ? (
                  <button
                    key={`${ri}-${ci}`}
                    onClick={() => playAudio(cell.kana)}
                    className="flex-1 flex flex-col items-center justify-center py-3 rounded-lg bg-white/5 hover:bg-white/15 active:scale-95 transition-all duration-150 min-h-[56px]"
                  >
                    <span className="text-lg font-bold text-white tracking-wider">
                      {cell.kana}
                    </span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">
                      {cell.romaji}
                    </span>
                  </button>
                ) : (
                  <div key={`${ri}-${ci}`} className="flex-1" />
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
